import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Lock } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge } from "../../components/dashboard/Shared";

export function Plans() {
  const [plans, setPlans] = useState(null);
  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => setPlans([]));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Subscription Plans" sub="Read-only — the plan catalogue is defined in code (subscription/plans.py). Assign or change per-user plans in the Subscriptions module.">
        <a href="/admin/subscriptions" className="btn-primary text-sm px-4 py-2" data-testid="go-to-subscriptions">Manage Subscribers →</a>
      </PageHeader>
      <div className="card-dark p-4 mb-4 flex items-center gap-2 text-xs text-gray-400">
        <Lock size={12} /> Plans are static. Edit code to change tier structure. Per-user overrides are set in the Subscriptions module.
      </div>
      {!plans ? <div className="skeleton h-64" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-plans-grid">
          {plans.map((p) => (
            <div key={p.code} data-testid={`admin-plan-${p.code.toLowerCase()}`}
                 className={`card-dark p-5 flex flex-col ${p.highlighted ? "border-[#7000FF]/40" : ""}`}>
              {p.highlighted && <p className="text-[10px] tracking-[0.25em] text-[#00F0FF] mb-2">MOST POPULAR</p>}
              <p className="text-sm text-gray-400 uppercase tracking-wider">{p.name}</p>
              <p className="tabular text-2xl text-white mt-2">
                {p.price_display}
                {p.period ? <span className="text-xs text-gray-500">/{p.period}</span> : null}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                MT5 limit: <span className="text-white">{p.mt5_limit === -1 ? "Unlimited" : p.mt5_limit}</span>
              </p>
              <ul className="mt-3 space-y-1 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="text-xs text-gray-400 flex gap-2">
                    <span className="text-[#00E676]">✓</span>{f}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[10px] text-gray-500">CTA: {p.cta}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Brokers() {
  const [brokers, setBrokers] = useState(null);
  const [form, setForm] = useState({ name: "", servers: "" });
  const load = () => api.get("/admin/brokers").then(({ data }) => setBrokers(data)).catch(() => setBrokers([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    await api.post("/admin/brokers", { name: form.name, servers: form.servers.split(",").map((s) => s.trim()).filter(Boolean) });
    toast.success("Broker added");
    setForm({ name: "", servers: "" });
    load();
  };
  const remove = async (b) => {
    if (!window.confirm(`Remove broker ${b.name}?`)) return;
    await api.delete(`/admin/brokers/${b.broker_id}`);
    load();
  };

  return (
    <div>
      <PageHeader title="Broker Management" sub="Approved brokers suggested during MT5 connection" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {!brokers ? <div className="skeleton h-64" /> : (
            <Table headers={["Broker", "Servers", "Status", ""]} testId="admin-brokers-table">
              {brokers.map((b) => (
                <tr key={b.broker_id} className="border-b border-white/5">
                  <Td className="text-white">{b.name}</Td>
                  <Td className="text-xs text-gray-500 !whitespace-normal">{b.servers.join(", ")}</Td>
                  <Td><StatusBadge status={b.status} /></Td>
                  <Td><button data-testid={`delete-broker-${b.broker_id}`} onClick={() => remove(b)} className="text-gray-400 hover:text-[#FF3B30]"><Trash2 size={15} /></button></Td>
                </tr>
              ))}
            </Table>
          )}
        </div>
        <form onSubmit={add} className="card-dark p-5 space-y-3 h-fit">
          <p className="label-xs">Add Broker</p>
          <input data-testid="broker-name-input" required className="input-dark" placeholder="Broker name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="broker-servers-input" className="input-dark" placeholder="Servers (comma separated)" value={form.servers} onChange={(e) => setForm({ ...form, servers: e.target.value })} />
          <button data-testid="broker-submit-button" className="btn-primary w-full py-2 text-sm">Add Broker</button>
        </form>
      </div>
    </div>
  );
}

export function AuditLogs() {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    api.get("/admin/audit-logs").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  }, []);
  return (
    <div>
      <PageHeader title="Audit Logs" sub="Every sensitive action, recorded" />
      {!logs ? <div className="skeleton h-96" /> : (
        <Table headers={["Time", "User", "Action", "Details", "IP"]} testId="audit-logs-table">
          {logs.map((l) => (
            <tr key={l.log_id} className="border-b border-white/5">
              <Td className="tabular text-xs text-gray-500">{String(l.created_at).slice(0, 19).replace("T", " ")}</Td>
              <Td className="text-xs">{l.email}</Td>
              <Td><span className="tabular text-xs text-[#00F0FF]">{l.action}</span></Td>
              <Td className="text-xs text-gray-500 !whitespace-normal max-w-md">{l.details}</Td>
              <Td className="tabular text-xs text-gray-600">{l.ip || "—"}</Td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export function Tickets() {
  const [tickets, setTickets] = useState(null);
  const [reply, setReply] = useState({});
  const load = () => api.get("/admin/tickets").then(({ data }) => setTickets(data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);

  const send = async (t) => {
    if (!reply[t.ticket_id]) return;
    await api.post(`/admin/tickets/${t.ticket_id}/reply`, { message: reply[t.ticket_id] });
    toast.success("Reply sent");
    setReply({ ...reply, [t.ticket_id]: "" });
    load();
  };
  const close = async (t) => {
    await api.put(`/admin/tickets/${t.ticket_id}/status`, { status: "closed" });
    load();
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Support Tickets" sub="Client and prospect enquiries" />
      {!tickets ? <div className="skeleton h-96" /> : tickets.map((t) => (
        <div key={t.ticket_id} className="card-dark p-5 mb-4" data-testid={`ticket-${t.ticket_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-white text-sm">{t.subject}</p>
            <div className="flex items-center gap-3">
              <StatusBadge status={t.status} />
              {t.status !== "closed" && <button data-testid={`close-ticket-${t.ticket_id}`} onClick={() => close(t)} className="text-xs text-gray-500 hover:text-white">Close</button>}
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">{t.name} · {t.email} · {String(t.created_at).slice(0, 10)}</p>
          <p className="text-sm text-gray-300 mb-3">{t.message}</p>
          {t.replies?.map((r, i) => (
            <div key={i} className="border-l-2 border-[#7000FF] pl-3 mb-2">
              <p className="text-xs text-[#00F0FF]">{r.from}</p>
              <p className="text-sm text-gray-400">{r.message}</p>
            </div>
          ))}
          {t.status !== "closed" && (
            <div className="flex gap-2 mt-3">
              <input data-testid={`reply-input-${t.ticket_id}`} className="input-dark flex-1" placeholder="Write a reply…" value={reply[t.ticket_id] || ""} onChange={(e) => setReply({ ...reply, [t.ticket_id]: e.target.value })} />
              <button data-testid={`reply-send-${t.ticket_id}`} onClick={() => send(t)} className="btn-primary px-4 text-sm">Send</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  const save = async (e) => {
    e.preventDefault();
    await api.put("/admin/settings", {
      platform_name: s.platform_name, default_fee_rate: parseFloat(s.default_fee_rate),
      support_email: s.support_email, maintenance_mode: s.maintenance_mode,
      upi_id: s.upi_id || "", usdt_trc20_address: s.usdt_trc20_address || "",
    });
    toast.success("Settings saved");
  };
  if (!s) return <div className="skeleton h-64" />;
  return (
    <div className="max-w-lg">
      <PageHeader title="Platform Settings" sub="Global configuration" />
      <form onSubmit={save} className="card-dark p-6 space-y-4">
        <div><label className="label-xs block mb-2">Platform name</label>
          <input data-testid="settings-name-input" className="input-dark" value={s.platform_name} onChange={(e) => setS({ ...s, platform_name: e.target.value })} /></div>
        <div><label className="label-xs block mb-2">Default performance fee (%)</label>
          <input data-testid="settings-fee-input" type="number" min="0" max="50" className="input-dark" value={s.default_fee_rate} onChange={(e) => setS({ ...s, default_fee_rate: e.target.value })} /></div>
        <div><label className="label-xs block mb-2">Support email</label>
          <input data-testid="settings-email-input" type="email" className="input-dark" value={s.support_email} onChange={(e) => setS({ ...s, support_email: e.target.value })} /></div>
        <div><label className="label-xs block mb-2">UPI / Bank ID (fee payments)</label>
          <input data-testid="settings-upi-input" className="input-dark" placeholder="yourname@upi" value={s.upi_id || ""} onChange={(e) => setS({ ...s, upi_id: e.target.value })} /></div>
        <div><label className="label-xs block mb-2">USDT TRC20 address (10% discount offer)</label>
          <input data-testid="settings-trc20-input" className="input-dark" placeholder="T..." value={s.usdt_trc20_address || ""} onChange={(e) => setS({ ...s, usdt_trc20_address: e.target.value })} /></div>
        <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer">
          <input data-testid="settings-maintenance-toggle" type="checkbox" checked={!!s.maintenance_mode} onChange={(e) => setS({ ...s, maintenance_mode: e.target.checked })} />
          Maintenance mode
        </label>
        <button data-testid="settings-save-button" className="btn-primary px-6 py-2.5 text-sm">Save Settings</button>
      </form>
    </div>
  );
}
